(() => {
if (window.SaludSubscriptionCartSync?.version === 'subscription-gifts-v3') return;
const subscriptionCart = {
  version: 'subscription-gifts-v3',
  tieredCollectionIds: ['496420290788', '496041001188', '413363241188'],
  bundleCollectionIds: ['413363241188'],
  collectionIdsByProduct: new Map(),
  productDataByHandle: new Map(),
  productDataCache: new Map(),
  planRefreshRequestedHandles: new Set(),
  bundlePlanSyncRunning: false,
  bundlePlanSyncQueued: false,
  bundlePlanSyncTimer: null,
  bundleCheckoutLockActive: false,
  subscriptionGiftSyncRunning: false,
  subscriptionGiftSyncQueued: false,
  subscriptionGiftSyncTimer: null,
  checkoutLockCount: 0,
  offers() {
    // Rebuy's Custom JavaScript can run without the theme config snippet.
    const offers = window.SaludSubscriptionConfig?.offers;
    return [...(Array.isArray(offers) ? offers : [
      { quantity: 1, orderDiscount: 20, defaultFrequency: 30, gifts: [] },
      { quantity: 2, orderDiscount: 25, defaultFrequency: 30, gifts: [] },
      { quantity: 3, orderDiscount: 30, defaultFrequency: 30, gifts: [] },
    ])]
      .filter((offer) => Number(offer.quantity) > 0)
      .sort((firstOffer, secondOffer) =>
        Number(firstOffer.quantity) - Number(secondOffer.quantity)
      );
  },

  offerForQuantity(quantity) {
    const requestedQuantity = Math.max(1, Number(quantity) || 1);
    const offers = this.offers();
    const eligibleOffers = offers.filter(
      (offer) => Number(offer.quantity) <= requestedQuantity
    );

    return eligibleOffers[eligibleOffers.length - 1] || offers[0] || null;
  },

  quantityKey(quantity) {
    return Number(this.offerForQuantity(quantity)?.quantity) || 1;
  },

  defaultFrequency() {
    return String(this.offerForQuantity(1)?.defaultFrequency || 30);
  },

  isSubscriptionGift(item) {
    return String(item?.properties?._subscription_gift || '').toLowerCase() === 'true';
  },

  isBundleBuilderGift(item) {
    return String(item?.properties?._bundle_builder_gift || '').toLowerCase() === 'true';
  },

  configuredSubscriptionGiftVariantIds() {
    return new Set(
      this.offers()
        .flatMap((offer) => offer.gifts || [])
        .map((gift) => String(gift?.variantId || gift?.variant_id || gift?.id || ''))
        .filter(Boolean)
    );
  },

  isRebuySubscriptionGift(item) {
    if (this.isBundleBuilderGift(item)) return false;
    if (this.isSubscriptionGift(item)) return true;

    const variantId = String(item?.variant_id || item?.variant?.id || '');
    return this.configuredSubscriptionGiftVariantIds().has(variantId);
  },

  orderedCartItems(items) {
    return [...(items || [])].sort((firstItem, secondItem) => (
      Number(this.isRebuySubscriptionGift(firstItem)) - Number(this.isRebuySubscriptionGift(secondItem))
    ));
  },

  applySmartCartItemOrder() {
    const smartCart = window.Rebuy?.SmartCart;
    if (!smartCart || smartCart.saludGiftItemOrderApplied || typeof smartCart.items !== 'function') return;

    const items = smartCart.items;
    smartCart.items = (...args) => this.orderedCartItems(items.apply(smartCart, args));
    smartCart.saludGiftItemOrderApplied = true;
  },

  subscriptionGiftLines(offer) {
    const gifts = new Map();

    (offer?.gifts || []).forEach((gift) => {
      const rawVariantId = gift?.variantId || gift?.variant_id || gift?.id;
      if (!rawVariantId) return;

      const variantId = Number(rawVariantId);
      if (!Number.isFinite(variantId)) return;

      gifts.set(variantId, (gifts.get(variantId) || 0) + 1);
    });

    return [...gifts].map(([id, quantity]) => ({
      id,
      quantity,
      properties: {
        _subscription_gift: 'true',
        _subscription_gift_offer: String(offer?.orderDiscount || offer?.quantity || ''),
        _subscription_gift_offer_id: String(offer?.id || offer?.handle || ''),
      },
    }));
  },

  subscriptionGiftQuantity(items) {
    return items.reduce((total, item) => {
      if (this.isRebuySubscriptionGift(item) || !this.isSubscriptionCartItem(item)) return total;

      return total + Number(item.quantity || 0);
    }, 0);
  },

  hasMatchingSubscriptionGifts(items, giftLines, offer) {
    const gifts = items.filter((item) => this.isRebuySubscriptionGift(item));
    const actualQuantities = new Map();
    const expectedQuantities = new Map(giftLines.map((gift) => [String(gift.id), gift.quantity]));
    const offerKey = String(offer?.orderDiscount || offer?.quantity || '');
    const offerId = String(offer?.id || offer?.handle || '');

    gifts.forEach((gift) => {
      const variantId = String(gift.variant_id || gift.variant?.id || '');
      actualQuantities.set(variantId, (actualQuantities.get(variantId) || 0) + Number(gift.quantity || 0));
    });

    if (actualQuantities.size !== expectedQuantities.size) return false;

    return [...expectedQuantities].every(([variantId, quantity]) => (
      actualQuantities.get(variantId) === quantity &&
      gifts
        .filter((gift) => String(gift.variant_id || gift.variant?.id || '') === variantId)
        .every((gift) => (
          String(gift.properties?._subscription_gift_offer || '') === offerKey &&
          String(gift.properties?._subscription_gift_offer_id || '') === offerId
        ))
    ));
  },

  scheduleSubscriptionGiftSync() {
    clearTimeout(this.subscriptionGiftSyncTimer);
    // Coalesce successive Rebuy events and read Shopify's current cart.
    this.subscriptionGiftSyncTimer = setTimeout(() => this.syncSubscriptionGifts(), 300);
  },

  async currentCart(sourceCart = null) {
    if (sourceCart?.items) return sourceCart;

    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Unable to load cart: ${response.status}`);

    return response.json();
  },

  async syncSubscriptionGifts(sourceCart = null) {
    if (this.subscriptionGiftSyncRunning) {
      this.subscriptionGiftSyncQueued = true;
      return;
    }

    this.subscriptionGiftSyncRunning = true;
    this.subscriptionGiftSyncQueued = false;
    this.lockCheckout();

    try {
      let cart = await this.currentCart(sourceCart);
      if (!cart?.items) return;

      const subscriptionQuantity = this.subscriptionGiftQuantity(cart.items);
      const offer = subscriptionQuantity > 0
        ? this.offerForQuantity(subscriptionQuantity)
        : null;
      const giftLines = offer ? this.subscriptionGiftLines(offer) : [];

      if (this.hasMatchingSubscriptionGifts(cart.items, giftLines, offer)) return;

      for (const gift of cart.items.filter((item) => this.isRebuySubscriptionGift(item))) {
        cart = await this.changeCartLine(gift, 0, null, gift.properties || {});
      }

      if (giftLines.length) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: giftLines }),
        });

        if (!response.ok) throw new Error(`Unable to add subscription gifts: ${response.status}`);

        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error(`Unable to load cart after adding subscription gifts: ${cartResponse.status}`);
        cart = await cartResponse.json();
      }

      this.refreshRebuy(cart);
    } catch (error) {
      console.error('Unable to synchronize subscription gifts.', error);
    } finally {
      this.subscriptionGiftSyncRunning = false;
      this.unlockCheckout();

      if (this.subscriptionGiftSyncQueued) {
        this.subscriptionGiftSyncQueued = false;
        this.scheduleSubscriptionGiftSync();
      }
    }
  },

  getCollectionIds(item) {
    const product = item?.product || {};
    const ids = [];

    if (Array.isArray(product.collection_ids)) ids.push(...product.collection_ids);
    if (Array.isArray(product.collections_ids)) ids.push(...product.collections_ids);
    if (Array.isArray(item?.collection_ids)) ids.push(...item.collection_ids);

    if (Array.isArray(product.collections)) {
      product.collections.forEach((collection) => {
        ids.push(typeof collection === 'object' && collection !== null ? collection.id : collection);
      });
    }

    const productId = String(item?.product_id || product.id || '');
    const collectionIds = ids.map(String);

    if (productId && collectionIds.length) {
      this.collectionIdsByProduct.set(productId, collectionIds);
    }

    return collectionIds.length
      ? collectionIds
      : this.collectionIdsByProduct.get(productId) || [];
  },

  isTieredProduct(item) {
    const tags = [item?.product?.tags, item?.tags]
      .flatMap((tagSource) => {
        if (Array.isArray(tagSource)) return tagSource;
        return typeof tagSource === 'string' ? tagSource.split(',') : [];
      })
      .map((tag) => String(tag).trim().toLowerCase());

    return (
      this.getCollectionIds(item).some((id) => this.tieredCollectionIds.includes(id)) ||
      tags.includes('bundle') ||
      this.hasTieredSellingPlans(item)
    );
  },

  hasTieredSellingPlans(item) {
    const product = this.productDataCache.get(this.productHandle(item));
    const configuredDiscounts = new Set(
      this.offers()
        .map((offer) => Number(offer.orderDiscount))
        .filter(Number.isFinite)
    );
    const variantId = String(item?.variant_id || item?.variant?.id || '');
    const variants = variantId
      ? (product?.variants || []).filter((variant) => String(variant.id) === variantId)
      : product?.variants || [];
    const matchingDiscounts = new Set();

    variants.forEach((variant) => {
      (variant.selling_plan_allocations || []).forEach((allocation) => {
        const discount = this.planDiscount(allocation, variant);
        if (configuredDiscounts.has(discount)) matchingDiscounts.add(discount);
      });
    });

    return matchingDiscounts.size > 1;
  },

  isBundleProduct(item) {
    return this.getCollectionIds(item).some((id) => this.bundleCollectionIds.includes(id));
  },

  isTieredSubscriptionItem(item) {
    return Boolean(
      item &&
        this.isTieredProduct(item) &&
        (this.currentSellingPlanId(item) || item?.product?.subscription)
    );
  },

  isSubscriptionCartItem(item) {
    return Boolean(
      item &&
        (
          this.currentSellingPlanId(item) ||
          item?.selling_plan_allocation ||
          item?.selling_plan ||
          item?.product?.subscription ||
          item?.product?.subscription_frequency
        )
    );
  },

  isBundleSubscriptionItem(item) {
    return Boolean(item && this.isBundleProduct(item) && this.currentSellingPlanId(item));
  },

  currentSellingPlanId(item) {
    return String(
      item?.selling_plan_allocation?.selling_plan?.id ||
        item?.selling_plan_allocation?.selling_plan_id ||
        item?.selling_plan_id ||
        item?.product?.selling_plan_id ||
        item?.product?.subscription?.selling_plan_id ||
        ''
    );
  },

  sellingPlanForItem(item, product) {
    const sellingPlanId = this.currentSellingPlanId(item);
    if (!sellingPlanId || !product) return null;

    return (product.selling_plan_groups || [])
      .flatMap((group) => group.selling_plans || [])
      .find((plan) => String(plan.id) === sellingPlanId) || null;
  },

  productHandle(item) {
    const directHandle = item?.product?.handle || item?.handle;
    if (directHandle) return String(directHandle);

    const productUrl = item?.product_url || item?.url || '';
    const match = String(productUrl).match(/\/products\/([^/?#]+)/);

    return match?.[1] || '';
  },

  async productData(item) {
    const handle = this.productHandle(item);
    if (!handle) return null;

    if (!this.productDataByHandle.has(handle)) {
      const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`)
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to load product ${handle}.`);
          return response.json();
        })
        .then((product) => {
          this.productDataCache.set(handle, product);
          return product;
        })
        .catch((error) => {
          this.productDataByHandle.delete(handle);
          console.error('Unable to load subscription plans.', error);
          return null;
        });

      this.productDataByHandle.set(handle, request);
    }

    return this.productDataByHandle.get(handle);
  },

  requestFrequencyRefresh(item) {
    const handle = this.productHandle(item);
    if (!handle || this.productDataCache.has(handle) || this.planRefreshRequestedHandles.has(handle)) return;

    this.planRefreshRequestedHandles.add(handle);
    this.productData(item).then((product) => {
      if (product && typeof window.Rebuy?.SmartCart?.refresh === 'function') {
        window.Rebuy.SmartCart.refresh();
      }
      if (!product) this.planRefreshRequestedHandles.delete(handle);
    });
  },

  preloadProductData() {
    const items = window.Rebuy?.Cart?.cart?.items || [];
    const productItems = items.filter((item) => this.productHandle(item));
    const uncachedHandles = new Set(productItems
      .map((item) => this.productHandle(item))
      .filter((handle) => !this.productDataCache.has(handle)));

    return Promise.all(
      productItems.map((item) => this.productData(item))
    ).then((products) => {
      if ([...uncachedHandles].some((handle) => this.productDataCache.has(handle)) && typeof window.Rebuy?.SmartCart?.refresh === 'function') {
        window.Rebuy.SmartCart.refresh();
      }

      return products;
    });
  },

  setCheckoutDisabled(disabled) {
    document
      .querySelectorAll(
        '#rebuy-cart [data-rebuy-component="checkout-area"] button, #rebuy-cart .rebuy-cart__checkout-button'
      )
      .forEach((button) => {
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
      });
  },

  syncCheckoutState() {
    this.setCheckoutDisabled(this.checkoutLockCount > 0);
  },

  lockCheckout() {
    this.checkoutLockCount += 1;
    this.setCheckoutDisabled(true);
  },

  unlockCheckout() {
    this.checkoutLockCount = Math.max(0, this.checkoutLockCount - 1);

    if (!this.checkoutLockCount) this.setCheckoutDisabled(false);
  },

  planFrequency(plan) {
    const values = [
      ...(plan?.options || []).map((option) => option.value),
      plan?.name,
    ];
    const planText = values.join(' ');
    const deliveryInterval = planText.match(/(?:every\s+)?(\d+)\s*days?\b/i);
    if (deliveryInterval) return deliveryInterval[1];

    const match = planText.match(/\d+/g);

    return match?.length ? match[match.length - 1] : '';
  },

  planDiscount(allocation, variant) {
    const regularPrice = Number(allocation.compare_at_price || variant?.price || 0);
    const planPrice = Number(allocation.price || 0);

    if (!regularPrice || planPrice > regularPrice) return null;

    return Math.round(((regularPrice - planPrice) / regularPrice) * 100);
  },

  frequency(item) {
    const cachedPlan = this.sellingPlanForItem(
      item,
      this.productDataCache.get(this.productHandle(item))
    );
    const cachedPlanFrequency = this.planFrequency(cachedPlan);
    if (cachedPlanFrequency) return cachedPlanFrequency;
    const planName =
      item?.selling_plan_allocation?.selling_plan?.name ||
      item?.selling_plan_allocation?.name ||
      '';
    const planFrequency = this.planFrequency({ name: planName });
    if (planFrequency) return planFrequency;

    return this.normalizeFrequency(item?.product?.subscription_frequency) || this.defaultFrequency();
  },

  async frequencyForItem(item) {
    const product = await this.productData(item);
    const sellingPlan = this.sellingPlanForItem(item, product);
    if (sellingPlan) return this.planFrequency(sellingPlan);
    const planName =
      item?.selling_plan_allocation?.selling_plan?.name ||
      item?.selling_plan_allocation?.name ||
      '';
    const frequency = this.planFrequency({ name: planName });
    if (frequency) return frequency;

    return '';
  },

  availableFrequencies(item) {
    const product = this.productDataCache.get(this.productHandle(item));
    const variantId = String(item?.variant_id || item?.variant?.id || '');
    const variant = product?.variants?.find((candidate) => String(candidate.id) === variantId);
    const quantity = this.isBundleSubscriptionItem(item)
      ? this.bundleTotalQuantity(window.Rebuy?.Cart?.cart?.items || [item])
      : item?.quantity;
    const discount = Number(this.offerForQuantity(quantity)?.orderDiscount);
    const currentFrequency = this.frequency(item);

    // Keep the selected value present while product plans are loading, or if
    // loading fails. Vue clears a select when its value has no matching option.
    if (!variant || !Number.isFinite(discount)) {
      this.requestFrequencyRefresh(item);
      return currentFrequency ? [currentFrequency] : [];
    }

    const plansById = new Map(
      (product.selling_plan_groups || [])
        .flatMap((group) => group.selling_plans || [])
        .map((plan) => [String(plan.id), plan])
    );
    const frequencies = (variant.selling_plan_allocations || [])
      .filter((allocation) => this.planDiscount(allocation, variant) === discount)
      .map((allocation) => this.planFrequency(plansById.get(String(allocation.selling_plan_id))))
      .filter(Boolean);

    if (currentFrequency) frequencies.push(currentFrequency);
    return [...new Set(frequencies)].sort((first, second) => Number(first) - Number(second));
  },

  subscriptionDiscount(item) {
    const discount = this.offerForQuantity(item?.quantity)?.orderDiscount;

    return Number.isFinite(Number(discount)) ? `${Number(discount)}%` : '';
  },

  subscriptionButtonLabel(item, fallbackLabel) {
    if (!this.isTieredProduct(item)) return fallbackLabel;

    const discount = this.subscriptionDiscount(item);
    if (!discount) return fallbackLabel;

    if (/\d+\s*%/.test(fallbackLabel)) {
      return fallbackLabel.replace(/\d+\s*%/g, discount);
    }

    return `SUBSCRIBE & SAVE ${discount}`;
  },

  switchToSubscription(item) {
    this.changeItem(item, item.quantity, this.defaultFrequency());
  },

  changeQuantity(item, quantity) {
    const nextQuantity = Math.max(1, Number(quantity) || 1);
    return this.changeItem(item, nextQuantity, null, !this.currentSellingPlanId(item));
  },

  changeFrequency(item, frequency) {
    this.changeItem(item, item.quantity, frequency);
  },

  async changeItem(item, quantity, frequency, onetime = false) {
    this.lockCheckout();

    try {
      const targetQuantity = this.isBundleSubscriptionItem(item)
        ? this.bundleTotalQuantity(window.Rebuy?.Cart?.cart?.items || [item]) - Number(item.quantity) + Number(quantity)
        : quantity;
      const targetFrequency = onetime ? '' : frequency || await this.frequencyForItem(item);
      const sellingPlanId = onetime ? null : targetFrequency && await this.sellingPlanId(item, targetQuantity, targetFrequency);
      if (!onetime && !sellingPlanId) return;

      const cart = await this.changeCartLine(
        item,
        quantity,
        sellingPlanId,
        onetime ? item.properties || {} : this.propertiesForQuantity(item, targetQuantity)
      );

      if (window.Rebuy?.Cart?.cart) window.Rebuy.Cart.cart = cart;
      await this.syncSubscriptionGifts(cart);
      this.refreshRebuy(window.Rebuy?.Cart?.cart || cart);
      this.scheduleBundleSellingPlanSync();
    } catch (error) {
      console.error('Unable to update the subscription item.', error);
    } finally {
      this.unlockCheckout();
    }
  },

  async changeCartLine(item, quantity, sellingPlanId, properties) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: item.key,
        quantity,
        selling_plan: sellingPlanId,
        properties,
      }),
    });

    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

    return response.json();
  },

  async sellingPlanId(item, quantity, frequency) {
    const offer = this.offerForQuantity(quantity);
    const product = await this.productData(item);
    const variantId = String(item?.variant_id || item?.variant?.id || '');
    const variant = product?.variants?.find((candidate) => String(candidate.id) === variantId);
    const targetFrequency = this.normalizeFrequency(frequency || offer?.defaultFrequency);
    const targetDiscount = Number(offer?.orderDiscount);

    if (!variant || !targetFrequency || !Number.isFinite(targetDiscount)) return null;

    const plansById = new Map(
      (product.selling_plan_groups || []).flatMap((group) => group.selling_plans || [])
        .map((plan) => [String(plan.id), plan])
    );

    const allocation = (variant.selling_plan_allocations || []).find((candidate) => {
      const plan = plansById.get(String(candidate.selling_plan_id));

      return (
        this.planFrequency(plan) === targetFrequency &&
        this.planDiscount(candidate, variant) === targetDiscount
      );
    });

    return allocation ? String(allocation.selling_plan_id) : null;
  },

  propertiesForQuantity(item, quantity) {
    const properties = Object.assign({}, item.properties || {});
    const discount = this.subscriptionDiscount({ quantity });

    if (discount) properties.Discount = discount;

    return properties;
  },

  bundleTotalQuantity(items) {
    return items.reduce((total, item) => {
      if (!this.isBundleSubscriptionItem(item)) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  },

  async bundleTargetState(item, totalQuantity) {
    const offer = this.offerForQuantity(totalQuantity);
    const frequency = await this.frequencyForItem(item);
    const discount = Number.isFinite(Number(offer?.orderDiscount))
      ? `${Number(offer.orderDiscount)}%`
      : '';

    return {
      sellingPlanId: frequency ? await this.sellingPlanId(item, offer?.quantity, frequency) : null,
      discount,
    };
  },

  async lineNeedsBundleUpdate(item, totalQuantity) {
    const target = await this.bundleTargetState(item, totalQuantity);
    if (!target.sellingPlanId) return false;

    return (
      this.currentSellingPlanId(item) !== String(target.sellingPlanId) ||
      item?.properties?.Discount !== target.discount
    );
  },

  scheduleBundleSellingPlanSync() {
    clearTimeout(this.bundlePlanSyncTimer);

    if (!this.bundleCheckoutLockActive) {
      this.bundleCheckoutLockActive = true;
      this.lockCheckout();
    }

    this.bundlePlanSyncTimer = setTimeout(() => this.syncBundleSellingPlans(), 50);
  },

  async syncBundleSellingPlans() {
    if (this.bundlePlanSyncRunning) {
      this.bundlePlanSyncQueued = true;
      return;
    }

    this.bundlePlanSyncRunning = true;
    this.bundlePlanSyncQueued = false;
    try {
      let cart = window.Rebuy?.Cart?.cart;
      let wasChanged = false;

      if (!cart?.items?.length) return;

      for (let attempt = 0; attempt < 20; attempt += 1) {
        const totalQuantity = this.bundleTotalQuantity(cart.items);
        if (totalQuantity === 0) break;

        let itemToUpdate = null;

        for (const item of cart.items) {
          if (
            this.isBundleSubscriptionItem(item) &&
            (await this.lineNeedsBundleUpdate(item, totalQuantity))
          ) {
            itemToUpdate = item;
            break;
          }
        }
        if (!itemToUpdate) break;

        const target = await this.bundleTargetState(itemToUpdate, totalQuantity);
        if (!target.sellingPlanId) break;
        const properties = Object.assign({}, itemToUpdate.properties || {}, {
          Discount: target.discount,
        });

        cart = await this.changeCartLine(
          itemToUpdate,
          itemToUpdate.quantity,
          target.sellingPlanId,
          properties
        );
        wasChanged = true;

        if (window.Rebuy?.Cart?.cart) window.Rebuy.Cart.cart = cart;
      }

      if (wasChanged) this.refreshRebuy(cart);
    } catch (error) {
      console.error('Unable to synchronize bundle selling plans.', error);
    } finally {
      this.bundlePlanSyncRunning = false;

      if (this.bundlePlanSyncQueued) {
        this.bundlePlanSyncQueued = false;
        this.scheduleBundleSellingPlanSync();
      } else if (this.bundleCheckoutLockActive) {
        this.bundleCheckoutLockActive = false;
        this.unlockCheckout();
      }
    }
  },

  normalizeFrequency(value) {
    const match = String(value || '').match(/\d+/g);
    return match?.length ? match[match.length - 1] : '';
  },

  refreshRebuy(cart) {
    if (window.Rebuy?.Cart?.cart) window.Rebuy.Cart.cart = cart;

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
    document.dispatchEvent(new CustomEvent('rebuy:cart.change', { detail: { cart } }));

    if (typeof window.Rebuy?.Cart?.fetchCart === 'function') {
      window.Rebuy.Cart.fetchCart();
    }

    requestAnimationFrame(() => this.syncCheckoutState());
  },
};
// Rebuy Custom JavaScript also assigns SaludRebuyCart. Keep the theme's
// controller independently so an older copy cannot disable gift reconciliation.
window.SaludSubscriptionCartSync = subscriptionCart;
window.SaludRebuyCart = subscriptionCart;

const syncRebuyCart = () => {
  const cart = subscriptionCart;
  window.SaludRebuyCart = cart;

  cart.applySmartCartItemOrder();
  cart.syncCheckoutState?.();
  cart.preloadProductData?.();
  cart.scheduleSubscriptionGiftSync();
  cart.scheduleBundleSellingPlanSync?.();
};

[
  'rebuy.loaded',
  'rebuy:cart.init',
  'rebuy:cart.ready',
  'rebuy:cart.add',
  'rebuy:cart.change',
  'rebuy:cart.enriched',
  'rebuy:smartcart.init',
  'rebuy:smartcart.ready',
  'rebuy:smartcart.show',
].forEach((eventName) => document.addEventListener(eventName, syncRebuyCart));

// Smart Cart V2 changes Shopify's cart through Ajax. This catches the actual
// completed cart request as a fallback for themes/Rebuy builds that don't emit
// a DOM event for a line-item action.
const saludFetch = window.fetch;
window.fetch = function saludRebuyCartFetch(...args) {
  const request = args[0];
  const url = typeof request === 'string' ? request : request?.url;
  const isCartMutation = /\/cart\/(?:add|change|update)\.js(?:[?#]|$)/.test(String(url || ''));
  const response = saludFetch.apply(this, args);

  if (isCartMutation) {
    response.then(
      () => window.SaludRebuyCart?.scheduleSubscriptionGiftSync?.(),
      () => window.SaludRebuyCart?.scheduleSubscriptionGiftSync?.()
    );
  }

  return response;
};

const saludXhrOpen = XMLHttpRequest.prototype.open;
const saludXhrSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.open = function saludRebuyCartOpen(method, url, ...args) {
  this.saludRebuyCartMutation = /\/cart\/(?:add|change|update)\.js(?:[?#]|$)/.test(String(url || ''));
  return saludXhrOpen.call(this, method, url, ...args);
};
XMLHttpRequest.prototype.send = function saludRebuyCartSend(...args) {
  if (this.saludRebuyCartMutation) {
    this.addEventListener('loadend', () => {
      window.SaludRebuyCart?.scheduleSubscriptionGiftSync?.();
    }, { once: true });
  }

  return saludXhrSend.apply(this, args);
};

syncRebuyCart();
})();
